import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { REGION_NAMES, citiesOf } from '@/shared/config/saudi-regions';
import { Gender } from '@/shared/api/generated/model';
import type { ReportStrandedOnBehalfCommand } from '@/shared/api/generated/model';

const inputClass =
  'w-full rounded-md border border-border bg-surface-elevated px-3 py-2 text-start outline-none focus:border-primary';

/**
 * The control room files a stranded report for somebody who telephoned (§23.7, request of
 * 21 September 2026).
 *
 * ⚠️ **The person on the telephone cannot press «أنا عالق»** — no application, no signal, or a
 * relative ringing on their behalf. Until this screen existed, the operator's only way to open a
 * rescue was to talk the caller through installing an app, which is not a thing to do to somebody
 * stuck in a wadi at night.
 *
 * ⚠️ **The position is typed, and that is the hard part of the call.** The operator reads it off the
 * caller's own phone, or off a location the family sends — so the two boxes are separate, labelled,
 * and refused outside the Kingdom by the server rather than silently opening a search in the sea.
 *
 * ⚠️ No national id and no chronic-illness question here: §23.9 keeps those for the Head of Control
 * and administration, and this form is filled while somebody is describing where they are stuck.
 */
export function ReportStrandedDialog({
  open,
  onOpenChange,
  onConfirm,
  busy,
  serverError,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (command: ReportStrandedOnBehalfCommand) => void;
  busy: boolean;
  serverError?: string | null;
}) {
  const { t } = useLocale();

  const [phone, setPhone] = useState('');
  const [fullName, setFullName] = useState('');
  const [gender, setGender] = useState<Gender>(Gender.Male);
  const [region, setRegion] = useState(REGION_NAMES[0]!);
  const [city, setCity] = useState(citiesOf(REGION_NAMES[0]!)[0]!);
  const [longitude, setLongitude] = useState('');
  const [latitude, setLatitude] = useState('');
  const [companions, setCompanions] = useState('0');
  const [needs, setNeeds] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setPhone('');
    setFullName('');
    setGender(Gender.Male);
    setRegion(REGION_NAMES[0]!);
    setCity(citiesOf(REGION_NAMES[0]!)[0]!);
    setLongitude('');
    setLatitude('');
    setCompanions('0');
    setNeeds('');
    setError(null);
  };

  const confirm = () => {
    const name = fullName.trim();
    if (name.length < 4) {
      setError(t.missions.strandedNameRequired);
      return;
    }

    // ⚠️ Only the shape, and the server has the real rule. A panel that decided what a Saudi mobile
    // number looks like would be a second opinion to keep in step with `PhoneNumber`.
    const dialled = phone.replace(/\s|-/g, '');
    if (dialled.length < 9) {
      setError(t.missions.strandedPhoneRequired);
      return;
    }

    const lon = Number(longitude);
    const lat = Number(latitude);
    if (!Number.isFinite(lon) || !Number.isFinite(lat) || (lon === 0 && lat === 0)) {
      setError(t.missions.strandedWhereRequired);
      return;
    }

    const withHim = companions.trim() === '' ? 0 : Number(companions);
    if (!Number.isInteger(withHim) || withHim < 0) {
      setError(t.missions.strandedCompanionsInvalid);
      return;
    }

    onConfirm({
      contactPhone: dialled,
      fullName: name,
      gender,
      region,
      city,
      longitude: lon,
      latitude: lat,
      companionsCount: withHim,
      needs: needs.trim() === '' ? null : needs.trim(),
    });

    reset();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent closeLabel={t.missions.closeAria}>
        <DialogHeader>
          <DialogTitle>{t.missions.strandedCreateTitle}</DialogTitle>
          <DialogDescription>{t.missions.strandedCreateLede}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t.missions.strandedNameLabel} htmlFor="stranded-name">
            <input
              id="stranded-name"
              autoFocus
              value={fullName}
              onChange={(event) => {
                setFullName(event.target.value);
                setError(null);
              }}
              className={inputClass}
            />
          </Field>

          {/* ⚠️ The number the rescue rings back, and the report is filed under it: a caller with no
              account is recorded by it, and the same number later reaches their own status card. */}
          <Field label={t.missions.strandedPhoneLabel} htmlFor="stranded-phone">
            <input
              id="stranded-phone"
              inputMode="tel"
              dir="ltr"
              placeholder="05XXXXXXXX"
              value={phone}
              onChange={(event) => {
                setPhone(event.target.value);
                setError(null);
              }}
              className={inputClass}
            />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label={t.missions.strandedGenderLabel} htmlFor="stranded-gender">
            <select
              id="stranded-gender"
              value={gender}
              onChange={(event) => setGender(event.target.value as Gender)}
              className={inputClass}
            >
              {Object.values(Gender).map((value) => (
                <option key={value} value={value}>
                  {t.missions.genders[value]}
                </option>
              ))}
            </select>
          </Field>

          <Field label={t.missions.subRegionLabel} htmlFor="stranded-region">
            <select
              id="stranded-region"
              value={region}
              onChange={(event) => {
                setRegion(event.target.value);
                setCity(citiesOf(event.target.value)[0]!);
              }}
              className={inputClass}
            >
              {REGION_NAMES.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </Field>

          <Field label={t.missions.subCityLabel} htmlFor="stranded-city">
            <select
              id="stranded-city"
              value={city}
              onChange={(event) => setCity(event.target.value)}
              className={inputClass}
            >
              {citiesOf(region).map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {/* ⚠️ Latitude first on screen, because that is the order a phone's own "my location"
              reads it out — and a pair entered the other way round is a search in another country,
              which the server refuses rather than opening. */}
          <Field label={t.missions.strandedLatitude} htmlFor="stranded-lat">
            <input
              id="stranded-lat"
              inputMode="decimal"
              dir="ltr"
              placeholder="29.8100"
              value={latitude}
              onChange={(event) => {
                setLatitude(event.target.value);
                setError(null);
              }}
              className={inputClass}
            />
          </Field>

          <Field label={t.missions.strandedLongitude} htmlFor="stranded-lon">
            <input
              id="stranded-lon"
              inputMode="decimal"
              dir="ltr"
              placeholder="39.8700"
              value={longitude}
              onChange={(event) => {
                setLongitude(event.target.value);
                setError(null);
              }}
              className={inputClass}
            />
          </Field>

          <Field label={t.missions.strandedCompanions} htmlFor="stranded-companions">
            <input
              id="stranded-companions"
              inputMode="numeric"
              dir="ltr"
              value={companions}
              onChange={(event) => {
                setCompanions(event.target.value);
                setError(null);
              }}
              className={inputClass}
            />
          </Field>
        </div>

        <Field label={t.missions.strandedNeeds} htmlFor="stranded-needs">
          <textarea
            id="stranded-needs"
            rows={2}
            value={needs}
            onChange={(event) => setNeeds(event.target.value)}
            placeholder={t.missions.strandedNeedsPlaceholder}
            className={inputClass}
          />
        </Field>

        {error ?? serverError ? (
          <p className="text-sm text-danger" role="alert">
            {error ?? serverError}
          </p>
        ) : null}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            {t.missions.cancel}
          </Button>
          <Button onClick={confirm} disabled={busy}>
            {busy ? t.missions.sending : t.missions.strandedConfirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
